import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pause } from "lucide-react";

const DEFAULT_PAUSE_MESSAGE = "Nous ne prenons pas de réservations en ce moment. Veuillez nous contacter directement par téléphone.";

interface PauseReservationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentMessage: string | null;
  savedMessage: string | null;
  onConfirm: (message: string) => Promise<void>;
}

export const PauseReservationsDialog = ({
  open,
  onOpenChange,
  currentMessage,
  savedMessage,
  onConfirm,
}: PauseReservationsDialogProps) => {
  // Use savedMessage as default if available, otherwise fall back to currentMessage or DEFAULT_PAUSE_MESSAGE
  const getInitialMessage = () => savedMessage || currentMessage || DEFAULT_PAUSE_MESSAGE;
  const [message, setMessage] = useState(getInitialMessage());
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset message when dialog opens - use savedMessage as default
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setMessage(savedMessage || currentMessage || DEFAULT_PAUSE_MESSAGE);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="w-5 h-5" />
            Mettre les réservations en pause
          </DialogTitle>
          <DialogDescription>
            Ce message sera affiché aux clients sur la page de réservation.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="pause-message">Message affiché aux clients</Label>
            <Textarea
              id="pause-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Entrez le message à afficher..."
              className="min-h-[120px] resize-none"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/500 caractères
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !message.trim()}
          >
            {isLoading ? "En cours..." : "Confirmer la mise en pause"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
